import { useApolloClient, useQuery } from '@apollo/client';
import { useCustomSnackbar } from '@procureai/ui-mui';
import { SetStateAction, useCallback, useEffect, useState } from 'react';

import { LOAD_APP_CONFIG_QUERY } from './LoadQuery.gql';
import { SAVE_APP_CONFIG_MUTATION } from './SaveQuery.gql';
import { AppConfigFormat, UseAppRemoteStateResult, UseAppRemoteStateUpdateReason } from './useAppRemoteState.types';
import { getInitialState, pickPersistableState } from './useAppRemoteState.util';

/**
 * safe/load the state from DB
 */
export const useAppRemoteState = (): UseAppRemoteStateResult => {
  const [state, setState] = useState(getInitialState);
  const { onError } = useCustomSnackbar();
  const client = useApolloClient();

  const { data: queryData, loading: queryLoading } = useQuery(LOAD_APP_CONFIG_QUERY, {
    onError,
  });

  const config = queryData?.appConfig[0];

  useEffect(() => {
    const { updateReason } = state;
    if (updateReason === UseAppRemoteStateUpdateReason.USER) {
      (async () => {
        try {
          await client.mutate({
            mutation: SAVE_APP_CONFIG_MUTATION,
            variables: {
              input: state.data,
            },
          });
        } catch (error) {
          console.log('useAppRemoteState save error', error);
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const updateState = useCallback((nextState: SetStateAction<AppConfigFormat>) => {
    setState((prev) => {
      return {
        ...prev,
        data: typeof nextState === 'function' ? nextState(prev.data) : nextState,
        updateReason: UseAppRemoteStateUpdateReason.USER,
      };
    });
  }, []);

  // update state if remote config loaded
  useEffect(() => {
    if (config) {
      setState((prev) => ({
        data: {
          ...prev.data,
          ...pickPersistableState(config),
        },
        updateReason: UseAppRemoteStateUpdateReason.REMOTE,
        loading: false,
      }));
    } else {
      setState((prev) => {
        if (prev.loading !== queryLoading) {
          return {
            ...prev,
            loading: queryLoading,
          };
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, queryLoading]);

  return [
    state.data,
    updateState,
    {
      loading: state.loading,
    },
  ];
};
